import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { ImportantNote, KeyTerm, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const statisticsUnit5Notes: UnitNotes = {
  unitId: "unit-5",
  unitName: "Unit 5: Sampling Distributions",
  examWeight: "7–12%",
  focus: "Probabilistic Reasoning and Simulation",
  sections: [
    {
      id: "topic-5-1",
      title: "Topic 5.1: Introducing Statistics: Why Is My Sample Not Like Yours?",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Concept of Sampling Variability</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              In statistics, we use a <strong>sample</strong> to make inferences about a <strong>population</strong>. However, different random samples drawn from the same population will usually produce different values.
            </p>
            <KeyTerm 
              term="Sampling Variability" 
              definition="The value of a statistic varies in repeated random sampling. This is not a mistake; it is the natural result of randomness."
            />
            <p className="text-slate-700 mt-4">
              <strong>The Goal:</strong> To distinguish between natural variation (chance) and meaningful differences.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Parameter vs. Statistic</h3>
            <p className="text-slate-700 leading-relaxed mb-4">This is the most fundamental distinction in inference.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Parameter</h4>
                <p className="text-sm text-slate-700 mt-1">A number that describes some characteristic of a <strong>population</strong>. In statistical practice, the value of a parameter is usually unknown.</p>
                <p className="text-sm text-slate-600 mt-2"><em>Notation:</em> <InlineMath math="\mu" /> (population mean), <InlineMath math="\sigma" /> (population SD), <InlineMath math="p" /> (population proportion)</p>
                <Mnemonic title="Memory Tip" content="Parameter → Population (both start with P)" />
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">Statistic</h4>
                <p className="text-sm text-slate-700 mt-1">A number that describes some characteristic of a <strong>sample</strong>. The value of a statistic can be computed directly from the sample data. We use statistics to estimate parameters.</p>
                <p className="text-sm text-slate-600 mt-2"><em>Notation:</em> <InlineMath math="\bar{x}" /> (sample mean), <InlineMath math="s" /> (sample SD), <InlineMath math="\hat{p}" /> (sample proportion)</p>
                <Mnemonic title="Memory Tip" content="Statistic → Sample (both start with S)" />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Three Distinct Distributions</h3>
            <p className="text-slate-700 leading-relaxed mb-4">Students often confuse these three. You must be able to distinguish them:</p>
            <div className="space-y-3">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">1. Population Distribution</h4>
                <p className="text-sm text-slate-700 mt-1">The distribution of values of the variable among all individuals in the population. (Describes individuals).</p>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">2. Distribution of Sample Data</h4>
                <p className="text-sm text-slate-700 mt-1">The distribution of values of the variable among all individuals in a specific sample. (Describes individuals).</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">3. Sampling Distribution</h4>
                <p className="text-sm text-slate-700 mt-1">The probability distribution of a <strong>statistic</strong> (like <InlineMath math="\bar{x}" /> or <InlineMath math="\hat{p}" />) that would occur if we took all possible samples of the same size <InlineMath math="n" /> from the same population. (Describes the statistic).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-2",
      title: "Topic 5.2: The Normal Distribution, Revisited",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            This topic reviews skills from Unit 1 to prepare for Normal Approximation in inference.
          </p>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Key Concepts</h3>
            <ul className="list-disc pl-6 space-y-3 text-slate-700">
              <li><strong>Normal Distribution:</strong> A continuous probability distribution characterized by a symmetric, bell-shaped density curve. Defined by mean (<InlineMath math="\mu" />) and standard deviation (<InlineMath math="\sigma" />).</li>
              <li>
                <strong>Z-Scores:</strong> A standardized score representing the number of standard deviations a value falls from the mean.
                <div className="mt-2 bg-slate-50 p-3 rounded border border-slate-200">
                  <BlockMath math="z = \frac{\text{value} - \text{mean}}{\text{standard deviation}}" />
                </div>
              </li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The 68-95-99.7 Rule (Empirical Rule)</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <ul className="list-disc pl-5 space-y-2 text-slate-700">
                <li>~<strong>68%</strong> of data falls within <InlineMath math="1\sigma" /> of <InlineMath math="\mu" /></li>
                <li>~<strong>95%</strong> of data falls within <InlineMath math="2\sigma" /> of <InlineMath math="\mu" /></li>
                <li>~<strong>99.7%</strong> of data falls within <InlineMath math="3\sigma" /> of <InlineMath math="\mu" /></li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Application in Unit 5</h3>
            <p className="text-slate-700">
              We will determine if a sampling distribution is "Approximately Normal." If it is, we can use Normal Probability calculations (Table A or calculator commands like <code className="bg-slate-200 px-1 rounded">normalcdf</code>) to calculate the probability of observing a specific statistic.
            </p>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-3",
      title: "Topic 5.3: The Central Limit Theorem (CLT)",
      content: (
        <div className="space-y-6">
          <ImportantNote>
            This is arguably the <strong>most important theorem</strong> in introductory statistics.
          </ImportantNote>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">What is the CLT?</h3>
            <KeyTerm 
              term="Central Limit Theorem" 
              definition="Draw an SRS of size n from any population with mean μ and finite standard deviation σ. When n is large, the sampling distribution of the sample mean x̄ is approximately Normal."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Conditions for Shape of Sampling Distribution of <InlineMath math="\bar{x}" /></h3>
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">If the Population is Normal</h4>
                <p className="text-sm text-slate-700 mt-1">The sampling distribution of <InlineMath math="\bar{x}" /> is <strong>exactly Normal</strong>, regardless of sample size <InlineMath math="n" />.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">If the Population is NOT Normal (Skewed/Uniform/Bimodal)</h4>
                <p className="text-sm text-slate-700 mt-1">The sampling distribution of <InlineMath math="\bar{x}" /> becomes <strong>approximately Normal</strong> if <InlineMath math="n" /> is "large enough."</p>
                <p className="text-sm text-amber-700 mt-2"><em>Rule of Thumb:</em> In AP Statistics, <strong><InlineMath math="n \ge 30" /></strong> satisfies the CLT.</p>
              </div>
            </div>
          </section>

          <ImportantNote>
            <strong>Common Misconception:</strong> The CLT does <strong>not</strong> say that the population becomes Normal. The population shape never changes. The CLT says that the distribution of <em>averages</em> (<InlineMath math="\bar{x}" />) becomes Normal.
          </ImportantNote>
        </div>
      )
    },
    {
      id: "topic-5-4",
      title: "Topic 5.4: Biased and Unbiased Point Estimates",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Point Estimator</h3>
            <KeyTerm 
              term="Point Estimator" 
              definition="A statistic that provides an estimate of a population parameter. Ideally, an estimator should have low bias and low variability."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Bias (Accuracy)</h3>
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">Unbiased Estimator</h4>
                <p className="text-sm text-slate-700 mt-1">A statistic is unbiased if the <strong>mean of its sampling distribution</strong> is equal to the true value of the parameter being estimated.</p>
                <ul className="list-disc pl-5 mt-2 text-sm text-slate-700">
                  <li><em>Unbiased:</em> Sample mean (<InlineMath math="\bar{x}" />) estimates <InlineMath math="\mu" />; Sample proportion (<InlineMath math="\hat{p}" />) estimates <InlineMath math="p" />.</li>
                  <li><em>Biased:</em> Sample Range (usually underestimates population range).</li>
                </ul>
              </div>
              <KeyTerm 
                term="Bias" 
                definition="Systematic deviation from the true parameter. It usually results from poor sampling techniques (like convenience sampling)."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Variability (Precision)</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li>The spread of the sampling distribution.</li>
              <li><strong>Key Fact:</strong> Variability of a statistic is determined by the <strong>sample size (<InlineMath math="n" />)</strong>.</li>
              <li>Larger samples (<InlineMath math="n" />) → Smaller spread (standard deviation) → More precise estimates.</li>
              <li><em>Note:</em> The size of the population does <em>not</em> affect variability, provided the population is at least 10 times larger than the sample.</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-5",
      title: "Topic 5.5: Sampling Distributions for Sample Proportions (p̂)",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            When we take repeated samples and calculate the proportion of successes (<InlineMath math="\hat{p}" />) for each, the resulting distribution is the sampling distribution of <InlineMath math="\hat{p}" />.
          </p>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Center, Spread, and Shape</h3>
            
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">1. Center (Mean)</h4>
                <BlockMath math="\mu_{\hat{p}} = p" />
                <p className="text-sm text-slate-600 mt-2">(The statistic <InlineMath math="\hat{p}" /> is an unbiased estimator of <InlineMath math="p" />).</p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">2. Spread (Standard Deviation)</h4>
                <BlockMath math="\sigma_{\hat{p}} = \sqrt{\frac{p(1-p)}{n}}" />
                <p className="text-sm text-amber-700 mt-2"><strong>10% Condition:</strong> This formula is only accurate if we sample with replacement or if the sample is small relative to the population (<InlineMath math="n \le 0.10N" />). This ensures independence of observations.</p>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">3. Shape</h4>
                <p className="text-sm text-slate-700 mt-1">Approximately Normal if the <strong>Large Counts Condition</strong> is met:</p>
                <div className="mt-2 bg-white p-3 rounded border border-purple-200">
                  <BlockMath math="np \ge 10 \quad \text{and} \quad n(1-p) \ge 10" />
                </div>
                <p className="text-sm text-slate-600 mt-2">(The expected number of successes and failures must both be at least 10).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-6",
      title: "Topic 5.6: Sampling Distributions for Differences in Sample Proportions",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            Used when comparing proportions from two independent populations (e.g., "Is the proportion of voters supporting Law A higher in City 1 than City 2?").
          </p>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Center, Spread, and Shape</h3>
            
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">1. Center (Mean)</h4>
                <BlockMath math="\mu_{\hat{p}_1 - \hat{p}_2} = p_1 - p_2" />
              </div>

              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">2. Spread (Standard Deviation)</h4>
                <p className="text-sm text-slate-700 mb-2">Since the two samples are independent, the <em>variances</em> add:</p>
                <BlockMath math="\sigma_{\hat{p}_1 - \hat{p}_2} = \sqrt{\frac{p_1(1-p_1)}{n_1} + \frac{p_2(1-p_2)}{n_2}}" />
                <p className="text-sm text-amber-700 mt-2"><strong>10% Condition:</strong> Must verify <InlineMath math="n_1 \le 0.10N_1" /> and <InlineMath math="n_2 \le 0.10N_2" />.</p>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">3. Shape</h4>
                <p className="text-sm text-slate-700 mt-1">Approximately Normal if the <strong>Large Counts Condition</strong> is met for <strong>both</strong> samples:</p>
                <div className="mt-2 bg-white p-3 rounded border border-purple-200 text-sm">
                  <InlineMath math="n_1p_1 \ge 10, \, n_1(1-p_1) \ge 10" /> <strong>AND</strong> <InlineMath math="n_2p_2 \ge 10, \, n_2(1-p_2) \ge 10" />
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-7",
      title: "Topic 5.7: Sampling Distributions for Sample Means (x̄)",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            When we take repeated samples and calculate the mean (<InlineMath math="\bar{x}" />) for each, the resulting distribution is the sampling distribution of <InlineMath math="\bar{x}" />.
          </p>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Center, Spread, and Shape</h3>
            
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">1. Center (Mean)</h4>
                <BlockMath math="\mu_{\bar{x}} = \mu" />
                <p className="text-sm text-slate-600 mt-2">(The statistic <InlineMath math="\bar{x}" /> is an unbiased estimator of <InlineMath math="\mu" />).</p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">2. Spread (Standard Deviation)</h4>
                <BlockMath math="\sigma_{\bar{x}} = \frac{\sigma}{\sqrt{n}}" />
                <ul className="list-disc pl-5 mt-2 text-sm text-slate-700">
                  <li><em>Analysis:</em> As <InlineMath math="n" /> increases, the standard deviation decreases (narrower curve). A sample mean of <InlineMath math="n=100" /> is much less variable than a single observation.</li>
                  <li><strong>10% Condition:</strong> Must verify <InlineMath math="n \le 0.10N" />.</li>
                </ul>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">3. Shape</h4>
                <ul className="list-disc pl-5 mt-2 text-sm text-slate-700">
                  <li><strong>If Population is Normal:</strong> Sampling distribution is Normal.</li>
                  <li><strong>If Population is NOT Normal:</strong> Sampling distribution is approximately Normal if <strong><InlineMath math="n \ge 30" /></strong> (Central Limit Theorem).</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-8",
      title: "Topic 5.8: Sampling Distributions for Differences in Sample Means",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            Used when comparing means from two independent populations (e.g., "Is the average height of plants in Group A different from Group B?").
          </p>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Center, Spread, and Shape</h3>
            
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">1. Center (Mean)</h4>
                <BlockMath math="\mu_{\bar{x}_1 - \bar{x}_2} = \mu_1 - \mu_2" />
              </div>

              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">2. Spread (Standard Deviation)</h4>
                <p className="text-sm text-slate-700 mb-2">Variances add:</p>
                <BlockMath math="\sigma_{\bar{x}_1 - \bar{x}_2} = \sqrt{\frac{\sigma_1^2}{n_1} + \frac{\sigma_2^2}{n_2}}" />
                <p className="text-sm text-amber-700 mt-2"><strong>10% Condition:</strong> Must verify <InlineMath math="n_1 \le 0.10N_1" /> and <InlineMath math="n_2 \le 0.10N_2" />.</p>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">3. Shape</h4>
                <p className="text-sm text-slate-700 mt-1">Approximately Normal if:</p>
                <ul className="list-disc pl-5 mt-2 text-sm text-slate-700">
                  <li>Both populations are Normal, <strong>OR</strong></li>
                  <li>Both sample sizes are large (<InlineMath math="n_1 \ge 30" /> and <InlineMath math="n_2 \ge 30" />) via CLT.</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "unit-5-summary",
      title: "Summary of Conditions Checklist",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            When asked to describe a sampling distribution or calculate a probability regarding a statistic, you must check:
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-bold text-blue-900 mb-3">For Proportions (<InlineMath math="\hat{p}" />)</h4>
              <ol className="list-decimal pl-5 space-y-2 text-sm text-slate-700">
                <li><strong>Random:</strong> Was the sample random? (Ensures unbiased center).</li>
                <li><strong>10% Condition:</strong> <InlineMath math="n \le 0.10N" /> (Allows calculation of <InlineMath math="\sigma" />).</li>
                <li><strong>Large Counts:</strong> <InlineMath math="np \ge 10" /> and <InlineMath math="n(1-p) \ge 10" /> (Allows assumption of Normality).</li>
              </ol>
            </div>

            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-bold text-green-900 mb-3">For Means (<InlineMath math="\bar{x}" />)</h4>
              <ol className="list-decimal pl-5 space-y-2 text-sm text-slate-700">
                <li><strong>Random:</strong> Was the sample random?</li>
                <li><strong>10% Condition:</strong> <InlineMath math="n \le 0.10N" />.</li>
                <li><strong>Normal/Large Sample:</strong>
                  <ul className="list-disc pl-5 mt-1">
                    <li>Population Normal? → Normal.</li>
                    <li>Population Unknown/Skewed? → Check <InlineMath math="n \ge 30" /> (CLT).</li>
                  </ul>
                </li>
              </ol>
            </div>
          </div>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">AP Exam Tips for Unit 5</h3>
            <ol className="list-decimal pl-6 space-y-3 text-slate-700">
              <li>
                <strong>Notation Matters:</strong> Do not mix up parameter symbols and statistic symbols.
                <ul className="list-disc pl-5 mt-1 text-sm">
                  <li className="text-red-700">Incorrect: "The mean of the sample is <InlineMath math="\mu = 5" />."</li>
                  <li className="text-green-700">Correct: "The mean of the sample is <InlineMath math="\bar{x} = 5" />."</li>
                </ul>
              </li>
              <li><strong>Square Root Error:</strong> When calculating the standard deviation of <InlineMath math="\bar{x}" />, students often forget to divide by <InlineMath math="\sqrt{n}" />. Remember: averages are less variable than individual observations.</li>
              <li><strong>Defining the CLT:</strong> If an FRQ asks you to explain <em>why</em> you can use a Normal calculation for a mean, do not just say "Because <InlineMath math="n \ge 30" />." You must explicitly reference the <strong>Central Limit Theorem</strong>.</li>
              <li><strong>Justify Independence:</strong> When calculating <InlineMath math="\sigma" />, explicitly state that you are checking the 10% condition to assume independence between sampled items.</li>
            </ol>
          </section>
        </div>
      )
    }
  ]
};

export default statisticsUnit5Notes;
